import mergeWith from 'lodash.mergewith';

/**
 * `background` is an ORDERED LAYER STACK, so a narrower scope must replace it
 * WHOLESALE rather than merge it slot-by-slot.
 *
 * lodash merges arrays index-by-index and never truncates, so a state that
 * defines FEWER layers than its base leaks the base's tail: base
 * `[image, colour]` + state `[red]` resolves to `[red, colour]`. It also merges
 * the layer OBJECTS key-by-key, so an image's `size`/`opacity` survive into a
 * colour that replaced it — and a stale `opacity: 0` then makes the replacement
 * invisible via the hide filter in `getBackgroundProperties`.
 *
 * This only bites where a state is applied by MERGING rather than by its own CSS
 * rule — i.e. the `active_state` path for `loading / success / error / disabled /
 * init`, which `blockList` in `styleHelper.ts` excludes from CSS emission.
 * `hover` / `click` / `focus` each get their own rule and are unaffected.
 *
 * Same hazard and same fix as the `replaceArrays` customizer in
 * `store/page/reducer.ts`; kept as a named helper so the nine components that
 * need it cannot drift apart.
 */
const replaceBackgroundArray = (_dstValue: unknown, srcValue: unknown, key: string) => (
  Array.isArray(srcValue) && key === 'background' ? [...srcValue] : undefined
);

/**
 * Drop-in replacement for `lodash.merge` when composing a node's style with one
 * of its states. Identical to a deep merge except that `background` replaces.
 *
 * A state that owns NO `background` is untouched by this — the key is absent, so
 * the base stack flows through and the fill inherits, which is exactly the
 * editor-side behaviour this mirrors (TNBEWEB-1869).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeStyleWithState = <TObject>(object: TObject, ...sources: any[]): TObject => (
  mergeWith(object, ...sources, replaceBackgroundArray)
);

export default mergeStyleWithState;
